import { ActionTree } from "vuex";
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";

import { DeskState } from "./types";
import { RootState } from "../../types";
import { mutationNames as mn } from "./mutations";
import { getterNames as gn } from "./getters";
import {
  FirebaseCollectionObserver,
  FirebaseDocumentObserver,
} from "../friends/types";
import { typeIsNotNone } from "../../../utils";

import { userId } from "../user";
import {
  ItemRef,
  Restriction,
  GameInfo,
  AnnouncementValues,
  AnnounceRestrictionParams,
  PlayCardParams,
  PlayCardApi,
  AnnounceRestrictionApi,
  StartGameApi,
  AnnouncementApi,
  AnnouncementParams,
  PovertyCheckChoiceParams,
  PovertyCheckChoiceApi,
  PovertyReturnCardsParams,
  PovertyReturnCardsApi,
  NextGameParams,
  NextGameApi,
  ClearTrickApi,
  CreateDeskApi,
  CARDS,
} from "@dokogd/shared";
import { createHttpFunction } from "../../../utils/firebase";

const create = "create";
const join = "join";
const listenForInvites = "listenForInvites";
const stopListenForInvites = "stopListenForInvites";
const listenOnDesk = "listenOnDesk";
const stopListenOnDesk = "stopListenOnDesk";
const listenForGame = "listenForGame";
const stopListenForGame = "stopListenForGame";
const startGame = "startGame";
const announceRestriction = "announceRestriction";
const povertyCheckChoice = "povertyCheckChoice";
const returnPovertyCards = "returnPovertyCards";
const playCard = "playCard";
const nextGame = "nextGame";
const sendAnnouncement = "sendAnnouncement";
const n = {
  create,
  join,
  listenForInvites,
  stopListenForInvites,
  listenOnDesk,
  stopListenOnDesk,
  listenForGame,
  stopListenForGame,
  startGame,
  announceRestriction,
  povertyCheckChoice,
  returnPovertyCards,
  playCard,
  nextGame,
  sendAnnouncement,
};

export const actions: ActionTree<DeskState, RootState> = {
  async [n.create](_context, payload) {
    console.log("desk.create", payload);
    const createDesk = await createHttpFunction(CreateDeskApi.name);
    return await createDesk(payload);
  },
  async [n.join](context, deskId: string) {
    const docRef = firebase.firestore().collection("desks").doc(deskId);

    const uid = userId(context.rootGetters);
    const updateData = {
      [`joined.${uid}`]: true,
    };
    console.log("join", deskId, updateData);
    await docRef.update(updateData);
    return deskId;
  },
  [n.listenForInvites](context) {
    const observer: FirebaseCollectionObserver = {
      next: (snapshot) => {
        const friends: ItemRef[] = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            console.log(data);
            if (data && typeof data.name === "string") {
              return {
                displayName: data.name,
                id: doc.id,
                avatarId:
                  data && data.lead && typeof data.lead.avatarId === "string"
                    ? data.lead.avatarId
                    : null,
              };
            } else {
              return null;
            }
          })
          .filter(typeIsNotNone);
        context.commit(mn.SET_INVITES, friends);
      },
      error: (err) => {
        console.error(n.listenForInvites, "observer-error", err);
      },
      complete: () => {
        console.log(n.listenForInvites, "observer-complete");
      },
    };

    const uid = userId(context.rootGetters);
    const millis =
      firebase.firestore.Timestamp.now().toMillis() - 1000 * 60 * 60;

    console.log("listen for user Id", uid, millis);
    context.commit(
      mn.SET_LISTENER_INVITE,
      firebase
        .firestore()
        .collection("desks")
        .where("participantIds", "array-contains", uid)
        .where(
          "createdAt",
          ">=",
          firebase.firestore.Timestamp.fromMillis(millis)
        )
        .orderBy("createdAt")
        .limit(10)
        .onSnapshot(observer)
    );
  },
  [n.stopListenForInvites](context) {
    context.commit(mn.SET_LISTENER_INVITE, null);
  },
  [n.listenOnDesk](context, deskId: string) {
    const observer: FirebaseDocumentObserver = {
      next: (snapshot) => {
        console.log("onDeskChange", snapshot.metadata);

        const deskData = snapshot.data();
        if (snapshot.exists && deskData) {
          context.commit(mn.SET_DESK, { data: deskData, id: snapshot.id });
          const hasAllPlayerJoined = context.getters[
            gn.hasAllPlayerJoined
          ] as boolean;
          const getGameCount = context.getters[gn.getGameCount] as number;
          const isGameLeader = context.getters[gn.isGameLeader] as boolean;
          if (hasAllPlayerJoined && getGameCount === 0 && isGameLeader) {
            context
              .dispatch(n.startGame, { deskId })
              .then((val) => console.log(n.startGame, val))
              .catch((err) => console.error(err));
          }
        } else {
          context.commit(mn.SET_DESK, null);
        }
      },
      error: (err) => {
        console.error(n.listenForInvites, "observer-error", err);
      },
      complete: () => {
        console.log(n.listenForInvites, "observer-complete");
      },
    };
    console.log("listen for desk Id", deskId);
    context.commit(
      mn.SET_LISTENER_DESK,
      firebase.firestore().collection("desks").doc(deskId).onSnapshot(observer)
    );
  },
  [n.stopListenOnDesk](context) {
    context.commit(mn.SET_LISTENER_DESK, null);
  },
  [n.listenForGame](context, deskId: string) {
    const observer: FirebaseCollectionObserver = {
      next: (snapshot) => {
        console.log("onGameChanged", snapshot.metadata);

        context.commit(mn.SET_LOADING_STATE, false);
        snapshot
          .docChanges({ includeMetadataChanges: false })
          .forEach((change) => {
            console.log("GAME CHANGE", change.type, change.doc.id);
            if (change.doc.id === "info") {
              if (change.type === "removed") {
                context.commit(mn.UPDATE_GAME_INFO, null);
              } else {
                const gameInfo = change.doc.data() as GameInfo;
                if (
                  gameInfo.player === "clear-trick" &&
                  context.state.desk?.lead?.id === userId(context.rootGetters)
                ) {
                  console.log("Start clear trick");
                  setTimeout(async () => {
                    const clearTrick = await createHttpFunction(
                      ClearTrickApi.name
                    );
                    clearTrick({ deskId: context.state.id })
                      .then((clearTrickRS) => {
                        console.log("clearTrick", clearTrickRS);
                      })
                      .catch((err) => {
                        console.error("clearTrick failed", err);
                      });
                  }, 1000);
                }
                context.commit(mn.UPDATE_GAME_INFO, gameInfo);
              }
            } else if (change.doc.id.startsWith("hand-")) {
              const id = change.doc.id.substr(5);
              const idx = parseInt(id, 10);
              context.commit(mn.PATCH_HAND, {
                idx,
                data: change.type === "removed" ? 10 : change.doc.data(),
              });
            }
          });
      },
      error: (err) => {
        console.error(n.listenForInvites, "observer-error", err);
      },
      complete: () => {
        console.log(n.listenForInvites, "observer-complete");
      },
    };

    const uid = userId(context.rootGetters);

    console.log("listen for game Id", uid, deskId);
    context.commit(
      mn.SET_LISTENER_GAME,
      firebase
        .firestore()
        .collection("desks")
        .doc(deskId)
        .collection("game")
        .where("participantIds", "array-contains", uid)

        .onSnapshot(observer)
    );
  },
  [n.stopListenForGame](context) {
    context.commit(mn.SET_LISTENER_GAME, null);
  },
  async [n.startGame](_context, deskId: string) {
    console.log("desk.startGame", deskId);
    const startGame = await createHttpFunction(StartGameApi.name);
    return await startGame(deskId);
  },
  async [n.announceRestriction](context, restriction: Restriction) {
    console.log("desk.announceRestriction", restriction, context);
    const deskId = context.state.id;

    if (deskId) {
      const params: AnnounceRestrictionParams = {
        deskId,
        restriction,
      };

      const announceRestriction = await createHttpFunction(
        AnnounceRestrictionApi.name
      );
      context.commit(mn.SET_LOADING_STATE, true);
      return await announceRestriction(params);
    } else {
      throw new Error("Expect selected DeskId");
    }
  },

  async [n.returnPovertyCards](context, cards: CARDS[]) {
    console.log("desk.returnPovertyCards", cards, context);
    const deskId = context.state.id;

    if (deskId) {
      const params: PovertyReturnCardsParams = {
        deskId,
        cards,
      };

      const httpFunction = await createHttpFunction(PovertyReturnCardsApi.name);
      context.commit(mn.SET_LOADING_STATE, true);

      return await httpFunction(params);
    } else {
      throw new Error("Expect selected DeskId");
    }
  },
  async [n.povertyCheckChoice](context, accept: boolean) {
    console.log("desk.povertyCheckChoice", accept, context);
    const deskId = context.state.id;

    if (deskId) {
      const params: PovertyCheckChoiceParams = {
        deskId,
        accept,
      };

      const httpFunction = await createHttpFunction(PovertyCheckChoiceApi.name);
      context.commit(mn.SET_LOADING_STATE, true);

      return await httpFunction(params);
    } else {
      throw new Error("Expect selected DeskId");
    }
  },
  async [n.playCard](context, card: CARDS) {
    console.log("desk.playCard", card, context);
    const deskId = context.state.id;

    if (deskId) {
      const params: PlayCardParams = {
        deskId,
        card,
      };

      const httpFunction = await createHttpFunction(PlayCardApi.name);
      context.commit(mn.SET_LOADING_STATE, true);

      return await httpFunction(params);
    } else {
      throw new Error("Expect selected DeskId");
    }
  },
  async [n.nextGame](context) {
    console.log("desk.nextGame", context);
    const deskId = context.state.id;

    if (deskId) {
      const params: NextGameParams = {
        deskId,
      };

      const httpFunction = await createHttpFunction(NextGameApi.name);
      context.commit(mn.SET_LOADING_STATE, true);

      return await httpFunction(params);
    } else {
      throw new Error("Expect selected DeskId");
    }
  },
  async [n.sendAnnouncement](context, value: AnnouncementValues) {
    console.log("desk.sendAnnouncement", value, context);
    const deskId = context.state.id;

    if (deskId) {
      const params: AnnouncementParams = {
        deskId,
        value,
      };

      const httpFunction = await createHttpFunction(AnnouncementApi.name);
      context.commit(mn.SET_LOADING_STATE, true);

      return await httpFunction(params);
    } else {
      throw new Error("Expect selected DeskId");
    }
  },
};

export const actionNames = n;
